import React, { useEffect, useMemo, useRef, useState } from 'react'
import * as Sentry from '@sentry/react'
import { Button } from '@/components/button/Button'
import { IconButton } from '@/components/icon-button/IconButton'
import { ClockStopwatch, XClose } from '@/assets/icons'
import { kPaxtonName } from '@/constants/constants-strings'
import { usePWAUpdate } from '@/context/pwa-update-context'
import { AppUpdateSource } from '@/analytics/enums/app-update.enums'
import {
  DEFAULT_PWA_UPDATE_POLICY,
  PWAUpdatePolicy,
  PWAUpdateSeverity,
  PWAUpdateTestMode,
  fetchPWAUpdatePolicy,
  normalizePWAUpdatePolicy,
} from '@/components/modals/pwa-update-policy'
import {
  DevTestPolicyOverrides,
  formatClock,
  formatCountdownBadge,
  getDevTestMode,
  getDevTestPolicyOverrides,
  getPWAUpdateStorageKey,
} from '@/components/modals/pwa-update-modal-helpers'

const COUNTDOWN_WARNING_MS = 5 * 60 * 1000
const APPLY_ATTEMPT_GRACE_MS = 10 * 60 * 1000
const reportedRecentApplyAttemptKeys = new Set<string>()

type UpdateUIState = 'hidden' | 'toast' | 'blocking'

const getNumberLS = (key: string): number | null => {
  const value = localStorage.getItem(key)
  if (value === null) return null

  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

const setNumberLS = (key: string, value: number | null) => {
  if (value === null) localStorage.removeItem(key)
  else localStorage.setItem(key, String(value))
}

const allowPWAUpdateTestParam = () => import.meta.env.MODE !== 'production'

const getDevTestSeverity = (mode: PWAUpdateTestMode | null): PWAUpdatePolicy['severity'] | null => {
  if (mode === null) return null
  return mode === PWAUpdateTestMode.Normal ? PWAUpdateSeverity.Normal : PWAUpdateSeverity.Critical
}

const shouldBypassSavedSnooze = (policy: PWAUpdatePolicy) => policy.severity === PWAUpdateSeverity.Critical

const hasRecentApplyAttempt = (policyStateVersion: string) => {
  const lastApplyAttemptAt = getNumberLS(getPWAUpdateStorageKey(policyStateVersion, 'lastApplyAttemptAt'))
  return lastApplyAttemptAt !== null && Date.now() - lastApplyAttemptAt < APPLY_ATTEMPT_GRACE_MS
}

const reportRecentApplyAttemptStillWaiting = (policyStateVersion: string, policy: PWAUpdatePolicy) => {
  if (reportedRecentApplyAttemptKeys.has(policyStateVersion)) return

  reportedRecentApplyAttemptKeys.add(policyStateVersion)

  void (async () => {
    const now = Date.now()
    const firstSeenAt = getNumberLS(getPWAUpdateStorageKey(policyStateVersion, 'firstSeenAt'))
    const lastApplyAttemptAt = getNumberLS(getPWAUpdateStorageKey(policyStateVersion, 'lastApplyAttemptAt'))
    const snoozeUntil = getNumberLS(getPWAUpdateStorageKey(policyStateVersion, 'snoozeUntil'))
    const registration = await navigator.serviceWorker?.getRegistration()

    Sentry.captureMessage('PWA update still waiting after recent apply attempt', {
      level: 'warning',
      tags: {
        feature: 'pwa-update',
        appVersion: APP_VERSION,
        updateSeverity: policy.severity,
      },
      extra: {
        policyStateVersion,
        deadlineMinutes: policy.deadlineMinutes ?? null,
        snoozeMinutes: policy.snoozeMinutes,
        firstSeenAgeMs: firstSeenAt === null ? null : now - firstSeenAt,
        lastApplyAttemptAgeMs: lastApplyAttemptAt === null ? null : now - lastApplyAttemptAt,
        snoozeRemainingMs: snoozeUntil === null ? null : snoozeUntil - now,
        hasController: Boolean(navigator.serviceWorker?.controller),
        controllerState: navigator.serviceWorker?.controller?.state ?? null,
        activeState: registration?.active?.state ?? null,
        waitingState: registration?.waiting?.state ?? null,
        installingState: registration?.installing?.state ?? null,
        path: window.location.pathname,
      },
    })
  })().catch((error) => {
    console.debug('Unable to capture PWA update diagnostics', error)
  })
}

const getDevTestOverrides = (): DevTestPolicyOverrides => {
  if (!allowPWAUpdateTestParam()) return {}

  const params = new URLSearchParams(window.location.search)
  return getDevTestPolicyOverrides(params)
}

const getDevTestPolicy = (policy: PWAUpdatePolicy, severity: PWAUpdatePolicy['severity'], overrides: DevTestPolicyOverrides = {}): PWAUpdatePolicy => {
  return normalizePWAUpdatePolicy({
    ...policy,
    severity,
    ...overrides,
  })
}

const PWAUpdateModal: React.FC = () => {
  const { updateAvailable, isApplying, applyFailureCount, applyUpdate } = usePWAUpdate()
  const devTestMode = useMemo(() => getDevTestMode(), [])
  const devTestSeverity = useMemo(() => getDevTestSeverity(devTestMode), [devTestMode])
  const devTestPolicyOverrides = useMemo(() => getDevTestOverrides(), [])
  const policyStateVersion = useMemo(() => (devTestMode ? `${APP_VERSION}-${devTestMode}-test-${Date.now()}` : APP_VERSION), [devTestMode])
  const devTestPolicy = useMemo(
    () => (devTestSeverity ? getDevTestPolicy(DEFAULT_PWA_UPDATE_POLICY, devTestSeverity, devTestPolicyOverrides) : null),
    [devTestPolicyOverrides, devTestSeverity]
  )
  const [policy, setPolicy] = useState<PWAUpdatePolicy>(devTestPolicy ?? DEFAULT_PWA_UPDATE_POLICY)
  const policyLoadedRef = useRef(devTestPolicy !== null)
  const [uiState, setUIState] = useState<UpdateUIState>('hidden')
  const [msUntilDeadline, setMsUntilDeadline] = useState<number | null>(null)
  const firstSeenAtRef = useRef<number | null>(null)

  useEffect(() => {
    if (applyFailureCount > 0) setUIState('toast')
  }, [applyFailureCount])

  useEffect(() => {
    if (!updateAvailable) return

    if (devTestSeverity) {
      void fetchPWAUpdatePolicy().then((policy) => {
        setPolicy(getDevTestPolicy(policy, devTestSeverity, devTestPolicyOverrides))
        policyLoadedRef.current = true
      })
      return
    }

    void fetchPWAUpdatePolicy().then((policy) => {
      setPolicy(policy)
      policyLoadedRef.current = true
    })
  }, [devTestPolicyOverrides, devTestSeverity, updateAvailable])

  const updateApp = () => void applyUpdate({ source: AppUpdateSource.Modal, policyStateVersion, simulateIfNoWorker: devTestPolicy !== null })

  useEffect(() => {
    if (!updateAvailable) {
      setUIState('hidden')
      firstSeenAtRef.current = null
      return
    }

    if (!policyLoadedRef.current) return

    if (devTestMode === PWAUpdateTestMode.CriticalNow) {
      firstSeenAtRef.current = Date.now()
      setMsUntilDeadline(0)
      setUIState('blocking')
      return
    }

    const firstSeenKey = getPWAUpdateStorageKey(policyStateVersion, 'firstSeenAt')
    const snoozeKey = getPWAUpdateStorageKey(policyStateVersion, 'snoozeUntil')
    const storedFirstSeen = getNumberLS(firstSeenKey)
    const firstSeenAt = storedFirstSeen ?? Date.now()
    firstSeenAtRef.current = firstSeenAt

    if (!storedFirstSeen) setNumberLS(firstSeenKey, firstSeenAt)

    const deadlineMs = policy.deadlineMinutes ? policy.deadlineMinutes * 60 * 1000 : null
    const msLeft = deadlineMs === null ? null : deadlineMs - (Date.now() - firstSeenAt)
    setMsUntilDeadline(msLeft === null ? null : Math.max(0, msLeft))

    if (msLeft !== null && msLeft <= 0) {
      if (hasRecentApplyAttempt(policyStateVersion)) {
        reportRecentApplyAttemptStillWaiting(policyStateVersion, policy)
        setUIState('toast')
        return
      }

      setUIState('blocking')
      setNumberLS(snoozeKey, null)
      return
    }

    const bypassSavedSnooze = shouldBypassSavedSnooze(policy)
    if (bypassSavedSnooze) {
      setNumberLS(snoozeKey, null)
    }

    const snoozeUntil = bypassSavedSnooze ? null : getNumberLS(snoozeKey)
    setUIState(snoozeUntil && Date.now() < snoozeUntil ? 'hidden' : 'toast')
  }, [devTestMode, policy, policyStateVersion, updateAvailable])

  useEffect(() => {
    if (devTestMode === PWAUpdateTestMode.CriticalNow) return
    if (!updateAvailable || !firstSeenAtRef.current || !policy.deadlineMinutes) return

    const firstSeenAt = firstSeenAtRef.current
    const snoozeKey = getPWAUpdateStorageKey(policyStateVersion, 'snoozeUntil')
    const deadlineMs = policy.deadlineMinutes * 60 * 1000

    const intervalId = window.setInterval(() => {
      const left = deadlineMs - (Date.now() - firstSeenAt)
      setMsUntilDeadline(Math.max(0, left))

      if (left <= 0) {
        if (hasRecentApplyAttempt(policyStateVersion)) {
          reportRecentApplyAttemptStillWaiting(policyStateVersion, policy)
          setUIState('toast')
          return
        }

        setNumberLS(snoozeKey, null)
        setUIState('blocking')
        return
      }

      if (left <= COUNTDOWN_WARNING_MS) {
        setNumberLS(snoozeKey, null)
        setUIState('toast')
        return
      }

      const snoozeUntil = getNumberLS(snoozeKey)
      if (!snoozeUntil || Date.now() >= snoozeUntil) setUIState('toast')
    }, 1000)

    return () => window.clearInterval(intervalId)
  }, [devTestMode, policy, policyStateVersion, updateAvailable])

  useEffect(() => {
    const check = async () => {
      if (document.visibilityState !== 'visible') return

      try {
        const registration = await navigator.serviceWorker?.getRegistration()
        await registration?.update()
      } catch (error) {
        console.debug('Unable to check for service worker updates', error)
      }
    }

    void check()
    document.addEventListener('visibilitychange', check)
    const intervalId = window.setInterval(check, 60 * 60 * 1000)

    return () => {
      document.removeEventListener('visibilitychange', check)
      window.clearInterval(intervalId)
    }
  }, [])

  const snooze = () => {
    console.debug('Snooze minutes', policy.snoozeMinutes)
    setNumberLS(getPWAUpdateStorageKey(policyStateVersion, 'snoozeUntil'), Date.now() + policy.snoozeMinutes * 60 * 1000)
    setUIState('hidden')
  }

  const updateCopy = `Refresh to load the latest version of ${kPaxtonName}, or an update will be required soon.`
  const blockingCopy = `Please refresh to continue using the latest version of ${kPaxtonName}.`
  const countdown = useMemo(() => (msUntilDeadline === null ? null : formatClock(msUntilDeadline)), [msUntilDeadline])
  const countdownBadge = useMemo(() => (msUntilDeadline === null ? null : formatCountdownBadge(msUntilDeadline)), [msUntilDeadline])
  const isCountdownWarning = msUntilDeadline !== null && msUntilDeadline <= COUNTDOWN_WARNING_MS
  const snoozeAllowed = !isCountdownWarning

  if (!updateAvailable || uiState === 'hidden') return null

  if (uiState === 'toast') {
    return (
      <div className="fixed inset-x-3 bottom-4 z-[999999] flex justify-center sm:inset-x-auto sm:right-6 sm:justify-end" aria-live="polite">
        <div className="w-full max-w-[344px] rounded-lg border border-border-default bg-surface-popover px-4 py-4 shadow-[0_8px_24px_rgba(0,0,0,0.14)] sm:max-w-[320px]">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-base font-semibold leading-6 text-text-primary">Update available</div>
              <p className="mt-1 text-sm leading-5 text-text-primary">{updateCopy}</p>
            </div>
            <IconButton
              aria-label="Dismiss update reminder"
              color="neutral"
              disabled={!snoozeAllowed || isApplying}
              icon={<XClose aria-hidden />}
              onClick={snooze}
              size="md"
              variant="plain"
              // -mr-1 -mt-1 offsets the 36px wrapper's 4px bleed so the visible button sits flush against the toast's top-right corner.
              wrapperClassName="-mr-1 -mt-1 shrink-0"
            />
          </div>
          <div className="mt-4 grid grid-cols-[1fr_auto_auto] items-center gap-3">
            {countdownBadge ? (
              <span
                className={`inline-flex h-6 w-fit items-center gap-1 rounded-full px-2 text-xs font-medium leading-none ring-1 ring-inset ${
                  isCountdownWarning ? 'bg-yellow-100 text-yellow-800 ring-yellow-400' : 'bg-surface-subtle text-brand-neutral-700 ring-border-default'
                }`}
                title={countdown ? `Required in ${countdown}` : undefined}
              >
                <ClockStopwatch className="size-3.5" aria-hidden />
                {countdownBadge}
              </span>
            ) : (
              <span />
            )}
            <Button variant="outlined" size="md" onClick={snooze} disabled={!snoozeAllowed || isApplying}>
              Later
            </Button>
            <Button size="md" onClick={updateApp} disabled={isApplying} loading={isApplying} aria-busy={isApplying}>
              {isApplying ? 'Refreshing...' : 'Refresh Now'}
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      className="fixed inset-0 z-[999999] flex items-center justify-center bg-black/50"
      role="dialog"
      aria-labelledby="pwa-update-title"
      aria-describedby="pwa-update-description"
    >
      <div className="relative mx-3 w-full max-w-[320px] rounded-lg bg-surface-emphasis px-6 py-7 text-center shadow-lg">
        <h2 id="pwa-update-title" className="text-xl font-semibold leading-7 text-text-primary">
          Update Required
        </h2>
        <p id="pwa-update-description" className="mt-3 text-base leading-6 text-text-primary">
          {blockingCopy}
        </p>
        <div className="mt-6 flex justify-center">
          <Button size="md" onClick={updateApp} disabled={isApplying} loading={isApplying} aria-busy={isApplying}>
            {isApplying ? 'Refreshing...' : 'Refresh and update'}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default PWAUpdateModal
